/**
 * Buscar en el historial, lo demás (the owner, 2026-10-06: «where else would need these types of
 * filters we should have full searchable histories where applicable honestly»): what was paid to
 * suppliers, the owner's money in and out, the tills gone by, the airtime sold and the cash taken
 * to the bank.
 *
 * Each was a short list of the latest on its screen, and whatever came before could not be found
 * at all. Each now opens whole on a screen of its own, found the way the sales are
 * (`lib/historial.ts`): the days, a name typed, the rest as dropdowns, every condition optional
 * and adding up. Pure, so the screens and their tests share one answer.
 */

import { fechaEnNicaragua, type Centavos } from '@abastece/shared';
import type { Compra, Deposito, Gasto, MovimientoDueno, PagoProveedor, Recarga, SesionCaja } from '@abastece/core';
import { pagoDeLaDeuda } from './gastos.js';
import { dentroDe, rangoAbierto, type Periodo, type Rango } from './historial.js';
import { coincide } from './texto.js';

/** The box and the days, which every one of these has. */
interface ConFechas {
  texto: string;
  periodo: Periodo;
  /** The two dates of `elegir`, as picked; empty for open. */
  desde: string;
  hasta: string;
}

const SIN_FECHAS: ConFechas = { texto: '', periodo: 'todo', desde: '', hasta: '' };

/** Voided or not: what stands, what was taken back, or both, the voided ones lined through. */
export type Vigencia = 'todos' | 'vigentes' | 'anulados';
export const VIGENCIAS: readonly Vigencia[] = ['todos', 'vigentes', 'anulados'];

const vigenciaCoincide = (anulado: boolean, v: Vigencia): boolean => v === 'todos' || (v === 'anulados') === anulado;

/** The latest day first, then the latest made: ids are made in time order. */
const reciente = (a: { fecha: string; id: string }, b: { fecha: string; id: string }): number =>
  b.fecha.localeCompare(a.fecha) || b.id.localeCompare(a.id);

/** Only the digits of a phone number, a receipt: «8888-7777» and «8888 7777» are one number. */
const digitos = (s: string): string => s.replace(/\D/g, '');

// ---------------------------------------------------------------------------
// Los pagos a proveedores
// ---------------------------------------------------------------------------

/** A payment's event as the log has it (`compra.pagada`, `gasto.pagado`), not refused. */
export interface EventoDePago {
  id: string;
  clientTs: string;
  payload: {
    compraId?: string;
    gastoId?: string;
    pagoId?: string;
    fecha: string;
    monto: number;
    formaPago: PagoProveedor['formaPago'];
    referencia?: string;
  };
}

/** A payment to a supplier, of a purchase or of an expense bought on credit. */
export interface PagoHecho {
  /** The event's, or the purchase's and the part's for a part paid with the purchase. */
  id: string;
  clientTs: string;
  fecha: string;
  monto: Centavos;
  formaPago: PagoProveedor['formaPago'];
  referencia: string | null;
  deuda: { tipo: 'compra' | 'gasto'; id: string };
  proveedorId: string | null;
  /** As its debt keeps it; undefined for one from before payments were named, which cannot be voided. */
  pago: PagoProveedor | undefined;
  /** Paid with the purchase itself, not after it. */
  conLaCompra: boolean;
  /** Paid with an expense bought on credit itself (2026-10-16), not after it. */
  conElGasto: boolean;
  /** Whether it can be voided on its own: a part paid with a purchase only while the rest is owed. */
  anulable: boolean;
  anulado: boolean;
}

/**
 * Every payment made to a supplier, the latest first: each one the log took, with the payment its
 * debt keeps of it — one the debt does not keep the books refused, though a pull marks every event
 * it brings confirmed (`pagoDeLaDeuda`) — and the parts paid with a purchase, which are payments
 * too: listed, so the trucker's cash is not paid twice. So are the parts of an expense on credit
 * (2026-10-16), paid against what it owed, which a later payment would otherwise pay again; an
 * expense paid in full in parts paid nobody a debt, and is in «Gastos».
 */
export function pagosHechos(
  eventos: readonly EventoDePago[],
  compras: ReadonlyMap<string, Compra>,
  gastos: ReadonlyMap<string, Gasto>,
): PagoHecho[] {
  const pagos: PagoHecho[] = [];
  for (const e of eventos) {
    const p = e.payload;
    const deuda = p.compraId ? compras.get(p.compraId) : gastos.get(p.gastoId ?? '');
    const pago = pagoDeLaDeuda(p.pagoId, deuda);
    if (pago === null) continue;
    pagos.push({
      id: e.id,
      clientTs: e.clientTs,
      fecha: p.fecha,
      monto: p.monto as Centavos,
      formaPago: p.formaPago,
      referencia: p.referencia ?? null,
      deuda: p.compraId ? { tipo: 'compra', id: p.compraId } : { tipo: 'gasto', id: p.gastoId ?? '' },
      proveedorId: deuda?.proveedorId ?? null,
      pago,
      conLaCompra: false,
      conElGasto: false,
      anulable: true,
      anulado: pago?.anulado === true,
    });
  }
  for (const c of compras.values()) {
    for (const x of c.pagos ?? []) {
      if (!x.conLaCompra) continue;
      pagos.push({
        id: `${c.id}:${x.id}`,
        clientTs: '',
        fecha: x.fecha,
        monto: x.monto,
        formaPago: x.formaPago,
        referencia: x.referencia,
        deuda: { tipo: 'compra', id: c.id },
        proveedorId: c.proveedorId,
        pago: x,
        conLaCompra: true,
        conElGasto: false,
        anulable: c.formaPago === 'credito' && !c.anulada,
        anulado: x.anulado,
      });
    }
  }
  for (const g of gastos.values()) {
    if (g.formaPago !== 'credito') continue;
    for (const x of g.pagos ?? []) {
      if (!x.conElGasto) continue;
      pagos.push({
        id: `${g.id}:${x.id}`,
        clientTs: '',
        fecha: x.fecha,
        monto: x.monto,
        formaPago: x.formaPago,
        referencia: x.referencia,
        deuda: { tipo: 'gasto', id: g.id },
        proveedorId: g.proveedorId,
        pago: x,
        conLaCompra: false,
        conElGasto: true,
        anulable: !g.anulado,
        anulado: x.anulado,
      });
    }
  }
  return pagos.sort((a, b) => b.fecha.localeCompare(a.fecha) || b.clientTs.localeCompare(a.clientTs));
}

/** The ways a supplier is paid, as «Pagos a proveedores» lists them: dollars only in a part paid with a purchase. */
export const FORMAS_DE_PAGO_PROVEEDOR: readonly PagoProveedor['formaPago'][] = [
  'efectivo_cordobas',
  'efectivo_dolares',
  'transferencia',
  'billetera',
];

export interface FiltroPagos extends ConFechas {
  /** Empty for any. */
  proveedorId: string;
  forma: PagoProveedor['formaPago'] | '';
  estado: Vigencia;
}

export const SIN_FILTROS_PAGOS: FiltroPagos = { ...SIN_FECHAS, proveedorId: '', forma: '', estado: 'todos' };

/** What the box looks through on a payment: who was paid, and what the payment was for. */
export interface ContextoPagos {
  proveedores: ReadonlyMap<string, string>;
  compras: ReadonlyMap<string, Compra>;
  gastos: ReadonlyMap<string, Gasto>;
}

/**
 * Every condition set holds for this payment. The box finds the supplier, the invoice of the
 * purchase paid or what the expense was, the payment's own receipt and why it was voided.
 */
export function pagoCoincide(p: PagoHecho, f: FiltroPagos, rango: Rango, ctx: ContextoPagos): boolean {
  if (!dentroDe(p.fecha, rango)) return false;
  if (f.proveedorId && p.proveedorId !== f.proveedorId) return false;
  if (f.forma && p.formaPago !== f.forma) return false;
  if (!vigenciaCoincide(p.anulado, f.estado)) return false;
  if (!f.texto.trim()) return true;
  const compra = p.deuda.tipo === 'compra' ? ctx.compras.get(p.deuda.id) : undefined;
  const gasto = p.deuda.tipo === 'gasto' ? ctx.gastos.get(p.deuda.id) : undefined;
  const texto = [
    (p.proveedorId && ctx.proveedores.get(p.proveedorId)) || '',
    compra?.documento ?? '',
    gasto?.descripcion ?? '',
    gasto?.referencia ?? '',
    p.referencia ?? '',
    p.pago?.motivoAnulacion ?? '',
  ].join(' ');
  return coincide(texto, f.texto);
}

/** The conditions set besides the box. */
export function filtrosDePagos(f: FiltroPagos, rango: Rango): (keyof FiltroPagos)[] {
  const puestos: (keyof FiltroPagos)[] = [];
  if (!rangoAbierto(rango)) puestos.push('periodo');
  if (f.proveedorId) puestos.push('proveedorId');
  if (f.forma) puestos.push('forma');
  if (f.estado !== 'todos') puestos.push('estado');
  return puestos;
}

/** How many were found, and what those not voided paid. */
export function resumenPagos(pagos: readonly PagoHecho[]): { n: number; total: Centavos } {
  return { n: pagos.length, total: pagos.reduce((a, p) => (p.anulado ? a : a + p.monto), 0) as Centavos };
}

// ---------------------------------------------------------------------------
// El dinero del dueño
// ---------------------------------------------------------------------------

export type TipoMovimiento = 'todos' | 'retiro' | 'aporte';
export const TIPOS_MOVIMIENTO: readonly TipoMovimiento[] = ['todos', 'retiro', 'aporte'];

export interface FiltroDueno extends ConFechas {
  tipo: TipoMovimiento;
  /** Where it came out of or went into, as the books name it: general cash, a bank account, the wallet; empty for any. */
  cuenta: string;
  estado: Vigencia;
}

export const SIN_FILTROS_DUENO: FiltroDueno = { ...SIN_FECHAS, tipo: 'todos', cuenta: '', estado: 'todos' };

/** Every condition set holds for this movement. The box finds what it was for and why it was voided. */
export function movimientoCoincide(m: MovimientoDueno, f: FiltroDueno, rango: Rango): boolean {
  if (!dentroDe(m.fecha, rango)) return false;
  if (f.tipo !== 'todos' && m.tipo !== f.tipo) return false;
  if (f.cuenta && m.cuenta !== f.cuenta) return false;
  if (!vigenciaCoincide(m.anulado === true, f.estado)) return false;
  return !f.texto.trim() || coincide([m.motivo ?? '', m.motivoAnulacion ?? ''].join(' '), f.texto);
}

export function filtrosDeDueno(f: FiltroDueno, rango: Rango): (keyof FiltroDueno)[] {
  const puestos: (keyof FiltroDueno)[] = [];
  if (!rangoAbierto(rango)) puestos.push('periodo');
  if (f.tipo !== 'todos') puestos.push('tipo');
  if (f.cuenta) puestos.push('cuenta');
  if (f.estado !== 'todos') puestos.push('estado');
  return puestos;
}

export const ordenarMovimientos = (ms: readonly MovimientoDueno[]): MovimientoDueno[] => [...ms].sort(reciente);

/** How many were found, and what the owner took out and put in, those voided aside. */
export function resumenDueno(ms: readonly MovimientoDueno[]): { n: number; retiros: Centavos; aportes: Centavos } {
  let retiros = 0;
  let aportes = 0;
  for (const m of ms) {
    if (m.anulado) continue;
    if (m.tipo === 'retiro') retiros += m.monto;
    else aportes += m.monto;
  }
  return { n: ms.length, retiros: retiros as Centavos, aportes: aportes as Centavos };
}

// ---------------------------------------------------------------------------
// Los turnos
// ---------------------------------------------------------------------------

/** What the counts found: all as expected, something missing, something over. */
export type ResultadoTurno = 'todos' | 'cuadro' | 'falto' | 'sobro';
export const RESULTADOS_TURNO: readonly ResultadoTurno[] = ['todos', 'cuadro', 'falto', 'sobro'];

/** A till's shift as its card needs it. */
export type Turno = Pick<
  SesionCaja,
  'id' | 'abiertaEn' | 'usuarioId' | 'diferenciaCordobas' | 'diferenciaDolares' | 'totalVendido'
> &
  Partial<Pick<SesionCaja, 'cerradaPor' | 'responsableId' | 'diferenciaEntregas' | 'diferenciaApertura'>>;

/** No box: a shift has no words of its own, and whose it was is found by name. */
export interface FiltroTurnos {
  periodo: Periodo;
  desde: string;
  hasta: string;
  /** Whoever had it: opened it, received it or closed it. Empty for anybody. */
  quien: string;
  resultado: ResultadoTurno;
}

export const SIN_FILTROS_TURNOS: FiltroTurnos = { periodo: 'todo', desde: '', hasta: '', quien: '', resultado: 'todos' };

/** The day a shift goes by: the day it opened, in Nicaragua. */
export const fechaDelTurno = (s: Pick<SesionCaja, 'abiertaEn'>): string => fechaEnNicaragua(s.abiertaEn);

/**
 * Every difference its card shows: at the corte, in córdobas and in dollars, at the handovers and
 * at opening. A shift is short if any was short, over if any was over — both, where the córdobas
 * were short and the dollars over — and squared only if none was off.
 */
function diferencias(s: Turno): number[] {
  return [s.diferenciaCordobas, s.diferenciaDolares, s.diferenciaEntregas, s.diferenciaApertura].map((d) => d ?? 0);
}

function resultadoCoincide(s: Turno, r: ResultadoTurno): boolean {
  switch (r) {
    case 'todos':
      return true;
    case 'cuadro':
      return diferencias(s).every((d) => d === 0);
    case 'falto':
      return diferencias(s).some((d) => d < 0);
    case 'sobro':
      return diferencias(s).some((d) => d > 0);
  }
}

export function turnoCoincide(s: Turno, f: FiltroTurnos, rango: Rango): boolean {
  if (!dentroDe(fechaDelTurno(s), rango)) return false;
  if (f.quien && s.usuarioId !== f.quien && s.cerradaPor !== f.quien && s.responsableId !== f.quien) return false;
  return resultadoCoincide(s, f.resultado);
}

export function filtrosDeTurnos(f: FiltroTurnos, rango: Rango): (keyof FiltroTurnos)[] {
  const puestos: (keyof FiltroTurnos)[] = [];
  if (!rangoAbierto(rango)) puestos.push('periodo');
  if (f.quien) puestos.push('quien');
  if (f.resultado !== 'todos') puestos.push('resultado');
  return puestos;
}

export const ordenarTurnos = <T extends Turno>(ts: readonly T[]): T[] =>
  [...ts].sort((a, b) => b.abiertaEn.localeCompare(a.abiertaEn) || b.id.localeCompare(a.id));

/** How many were found, what they sold, and what their cortes were off by in córdobas, together. */
export function resumenTurnos(ts: readonly Turno[]): { n: number; vendido: Centavos; diferencia: Centavos } {
  return {
    n: ts.length,
    vendido: ts.reduce((a, s) => a + s.totalVendido, 0) as Centavos,
    diferencia: ts.reduce((a, s) => a + (s.diferenciaCordobas ?? 0), 0) as Centavos,
  };
}

// ---------------------------------------------------------------------------
// Las recargas
// ---------------------------------------------------------------------------

/** Paid as it was sold, still waiting for the transfer to be seen, or the transfer never came. */
export type EstadoRecarga = 'todas' | Recarga['estadoPago'];
export const ESTADOS_RECARGA: readonly EstadoRecarga[] = ['todas', 'confirmado', 'pendiente', 'rechazado'];

/** The ways airtime is paid, in the order its form offers them. */
export const FORMAS_DE_RECARGA = ['efectivo_cordobas', 'transferencia', 'billetera'] as const;

export interface FiltroRecargas extends ConFechas {
  /** Empty for any. */
  operadora: string;
  forma: string;
  estado: EstadoRecarga;
}

export const SIN_FILTROS_RECARGAS: FiltroRecargas = { ...SIN_FECHAS, operadora: '', forma: '', estado: 'todas' };

/**
 * Every condition set holds for this top-up. The box finds the phone number — however its dashes
 * and spaces were typed, on either side — the carrier, and the transfer's receipt.
 */
export function recargaCoincide(r: Recarga, f: FiltroRecargas, rango: Rango): boolean {
  if (!dentroDe(r.fecha, rango)) return false;
  if (f.operadora && r.operadora !== f.operadora) return false;
  if (f.forma && r.formaPago !== f.forma) return false;
  if (f.estado !== 'todas' && r.estadoPago !== f.estado) return false;
  const q = f.texto.trim();
  if (!q) return true;
  const numero = digitos(q);
  if (numero.length >= 3 && numero.length === q.replace(/[\s-]/g, '').length && digitos(r.telefono).includes(numero)) return true;
  return coincide([r.operadora, r.telefono, r.referencia ?? ''].join(' '), q);
}

export function filtrosDeRecargas(f: FiltroRecargas, rango: Rango): (keyof FiltroRecargas)[] {
  const puestos: (keyof FiltroRecargas)[] = [];
  if (!rangoAbierto(rango)) puestos.push('periodo');
  if (f.operadora) puestos.push('operadora');
  if (f.forma) puestos.push('forma');
  if (f.estado !== 'todas') puestos.push('estado');
  return puestos;
}

export const ordenarRecargas = (rs: readonly Recarga[]): Recarga[] => [...rs].sort(reciente);

/** How many were found, and what was sold and earned: a top-up whose transfer never came, neither. */
export function resumenRecargas(rs: readonly Recarga[]): { n: number; vendido: Centavos; comision: Centavos } {
  const cobradas = rs.filter((r) => r.estadoPago !== 'rechazado');
  return {
    n: rs.length,
    vendido: cobradas.reduce((a, r) => a + r.montoFacial, 0) as Centavos,
    comision: cobradas.reduce((a, r) => a + r.comision, 0) as Centavos,
  };
}

// ---------------------------------------------------------------------------
// Los depósitos
// ---------------------------------------------------------------------------

/** Córdobas in it, dollars in it: a deposit can carry both. */
export type MonedaDeposito = 'todas' | 'cordobas' | 'dolares';
export const MONEDAS_DEPOSITO: readonly MonedaDeposito[] = ['todas', 'cordobas', 'dolares'];

export interface FiltroDepositos extends ConFechas {
  moneda: MonedaDeposito;
  estado: Vigencia;
}

export const SIN_FILTROS_DEPOSITOS: FiltroDepositos = { ...SIN_FECHAS, moneda: 'todas', estado: 'todos' };

/** Every condition set holds for this deposit. The box finds the slip's number, the note and why it was voided. */
export function depositoCoincide(d: Deposito, f: FiltroDepositos, rango: Rango): boolean {
  if (!dentroDe(d.fecha, rango)) return false;
  if (f.moneda === 'cordobas' && d.cordobas <= 0) return false;
  if (f.moneda === 'dolares' && d.dolares <= 0) return false;
  if (!vigenciaCoincide(d.anulado === true, f.estado)) return false;
  return !f.texto.trim() || coincide([d.referencia ?? '', d.nota ?? '', d.motivoAnulacion ?? ''].join(' '), f.texto);
}

export function filtrosDeDepositos(f: FiltroDepositos, rango: Rango): (keyof FiltroDepositos)[] {
  const puestos: (keyof FiltroDepositos)[] = [];
  if (!rangoAbierto(rango)) puestos.push('periodo');
  if (f.moneda !== 'todas') puestos.push('moneda');
  if (f.estado !== 'todos') puestos.push('estado');
  return puestos;
}

export const ordenarDepositos = (ds: readonly Deposito[]): Deposito[] => [...ds].sort(reciente);

/** How many were found, and what those not voided took to the bank, each currency on its own. */
export function resumenDepositos(ds: readonly Deposito[]): { n: number; cordobas: Centavos; dolares: Centavos } {
  const vigentes = ds.filter((d) => !d.anulado);
  return {
    n: ds.length,
    cordobas: vigentes.reduce((a, d) => a + d.cordobas, 0) as Centavos,
    dolares: vigentes.reduce((a, d) => a + d.dolares, 0) as Centavos,
  };
}
